import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import {
    HeartOutlined,
    ExperimentOutlined,
    MedicineBoxOutlined,
    CalendarOutlined,
    DashboardOutlined,
    LineChartOutlined,
    RightOutlined,
    SearchOutlined,
    AppstoreOutlined
} from "@ant-design/icons";

import {
    canSyncHealthData,
    getAllHealthHistory
} from "../../api/healthTracking";

import {
    extraHealthTools,
    findExtraHealthToolByType
} from "./extraTools";


export default function TienIchSucKhoe() {

    const [recentData, setRecentData] =
        useState([]);

    const [loadingRecent, setLoadingRecent] =
        useState(true);

    const [search, setSearch] =
        useState("");

    const syncWithAccount =
        canSyncHealthData();


    const oldTools = [
        {
            name: "Theo dõi huyết áp",
            description:
                "Ghi lại huyết áp và theo dõi sự thay đổi theo thời gian",
            icon: <HeartOutlined />,
            color:
                "bg-red-50 text-red-500",
            link:
                "/tienich/suc-khoe/huyet-ap"
        },
        {
            name: "Theo dõi đường huyết",
            description:
                "Ghi lại chỉ số đường huyết theo từng thời điểm",
            icon: <ExperimentOutlined />,
            color:
                "bg-rose-50 text-rose-500",
            link:
                "/tienich/suc-khoe/duong-huyet"
        },
        {
            name: "Theo dõi SpO₂",
            description:
                "Theo dõi độ bão hòa oxy trong máu",
            icon: <DashboardOutlined />,
            color:
                "bg-blue-50 text-blue-600",
            link:
                "/tienich/suc-khoe/spo2"
        },
        {
            name: "Theo dõi nhịp tim",
            description:
                "Ghi lại và theo dõi nhịp tim hằng ngày",
            icon: <LineChartOutlined />,
            color:
                "bg-pink-50 text-pink-500",
            link:
                "/tienich/suc-khoe/nhip-tim"
        },
        {
            name: "Lịch uống thuốc",
            description:
                "Quản lý lịch dùng thuốc và nhắc lịch hằng ngày",
            icon: <MedicineBoxOutlined />,
            color:
                "bg-green-50 text-green-600",
            link:
                "/tienich/suc-khoe/lich-thuoc"
        },
        {
            name: "Lịch tái khám",
            description:
                "Theo dõi lịch khám, tái khám và các cuộc hẹn sức khỏe",
            icon: <CalendarOutlined />,
            color:
                "bg-purple-50 text-purple-600",
            link:
                "/tienich/suc-khoe/lich-tai-kham"
        }
    ];


    const newTools =
        extraHealthTools.map(
            (tool) => ({
                name: tool.name,
                description:
                    tool.description,
                icon: tool.icon,
                color:
                    "bg-cyan-50 text-blue-600",
                link:
                    `/tienich/suc-khoe/theo-doi/${tool.slug}`
            })
        );


    const tools = [
        ...oldTools,
        ...newTools
    ];


    const filteredTools =
        useMemo(() => {

            const q =
                search
                    .trim()
                    .toLocaleLowerCase(
                        "vi"
                    );

            if (!q) {
                return tools;
            }

            return tools.filter(
                (item) =>
                    (
                        item.name +
                        " " +
                        item.description
                    )
                        .toLocaleLowerCase(
                            "vi"
                        )
                        .includes(q)
            );

        }, [search]);


    const getOldTrackingInfo =
        (item) => {

            const data =
                item?.tracking_data ||
                {};

            switch (
                item.tracking_type
            ) {

                case "huyet_ap":
                    return {
                        name: "Huyết áp",
                        value:
                            data.systolic &&
                            data.diastolic
                                ? `${data.systolic}/${data.diastolic} mmHg`
                                : "Đã lưu chỉ số",
                        extra:
                            data.heartRate
                                ? `Nhịp tim: ${data.heartRate} lần/phút`
                                : "",
                        icon:
                            <HeartOutlined />,
                        color:
                            "bg-red-50 text-red-500",
                        link:
                            "/tienich/suc-khoe/huyet-ap"
                    };


                case "duong_huyet":
                    return {
                        name: "Đường huyết",
                        value:
                            data.glucose
                                ? `${data.glucose} mmol/L`
                                : "Đã lưu chỉ số",
                        extra:
                            data.timeTypeLabel ||
                            "",
                        icon:
                            <ExperimentOutlined />,
                        color:
                            "bg-rose-50 text-rose-500",
                        link:
                            "/tienich/suc-khoe/duong-huyet"
                    };


                case "spo2":
                    return {
                        name: "SpO₂",
                        value:
                            data.spo2
                                ? `${data.spo2}%`
                                : "Đã lưu chỉ số",
                        extra:
                            data.heartRate
                                ? `Nhịp tim: ${data.heartRate} lần/phút`
                                : "",
                        icon:
                            <DashboardOutlined />,
                        color:
                            "bg-blue-50 text-blue-600",
                        link:
                            "/tienich/suc-khoe/spo2"
                    };


                case "nhip_tim":
                    return {
                        name: "Nhịp tim",
                        value:
                            data.heartRate
                                ? `${data.heartRate} lần/phút`
                                : "Đã lưu chỉ số",
                        extra:
                            data.statusLabel ||
                            "",
                        icon:
                            <LineChartOutlined />,
                        color:
                            "bg-pink-50 text-pink-500",
                        link:
                            "/tienich/suc-khoe/nhip-tim"
                    };


                case "lich_thuoc":
                    return {
                        name:
                            "Lịch uống thuốc",
                        value:
                            data.medicineName ||
                            "Đã thêm lịch thuốc",
                        extra:
                            data.time
                                ? `Thời gian: ${data.time}`
                                : "",
                        icon:
                            <MedicineBoxOutlined />,
                        color:
                            "bg-green-50 text-green-600",
                        link:
                            "/tienich/suc-khoe/lich-thuoc"
                    };


                case "lich_tai_kham":
                    return {
                        name:
                            "Lịch tái khám",
                        value:
                            data.appointmentDate
                                ? `Ngày ${formatAppointmentDate(
                                    data.appointmentDate
                                )}`
                                : "Đã thêm lịch tái khám",
                        extra:
                            data.appointmentTime
                                ? `Giờ hẹn: ${data.appointmentTime}`
                                : data.place ||
                                  "",
                        icon:
                            <CalendarOutlined />,
                        color:
                            "bg-purple-50 text-purple-600",
                        link:
                            "/tienich/suc-khoe/lich-tai-kham"
                    };


                default:
                    return null;
            }
        };


    const getNewValue =
        (
            tool,
            data
        ) => {

            switch (tool.type) {

                case "can_nang_bmi": {

                    const w =
                        Number(
                            data.weight
                        );

                    const h =
                        Number(
                            data.height
                        ) / 100;

                    const bmi =
                        w > 0 &&
                        h > 0
                            ? w /
                              (h * h)
                            : null;

                    return (
                        `${data.weight || "-"} kg` +
                        (
                            bmi
                                ? ` · BMI ${bmi.toFixed(1)}`
                                : ""
                        )
                    );
                }


                case "nhiet_do":
                    return data.temperature
                        ? `${data.temperature} °C`
                        : "Đã lưu";


                case "nhip_tho":
                    return data.respiratoryRate
                        ? `${data.respiratoryRate} lần/phút`
                        : "Đã lưu";


                case "vong_eo":
                    return data.waist
                        ? `${data.waist} cm`
                        : "Đã lưu";


                case "giac_ngu":
                    return data.sleepHours !==
                        undefined
                        ? `${data.sleepHours} giờ`
                        : "Đã lưu";


                case "luong_nuoc":
                    return data.water !==
                        undefined
                        ? `${data.water} mL`
                        : "Đã lưu";


                case "trieu_chung":
                    return (
                        data.symptom ||
                        "Đã lưu triệu chứng"
                    );


                case "van_dong":
                    return (
                        data.activity ||
                        "Đã lưu vận động"
                    );


                case "buoc_chan":
                    return data.steps !==
                        undefined
                        ? `${data.steps} bước`
                        : "Đã lưu";


                case "ghi_chu_suc_khoe":
                    return (
                        data.content ||
                        "Đã lưu ghi chú"
                    );


                default:
                    return "Đã lưu";
            }
        };


    const getTrackingInfo =
        (item) => {

            const old =
                getOldTrackingInfo(
                    item
                );

            if (old) {
                return old;
            }


            const tool =
                findExtraHealthToolByType(
                    item.tracking_type
                );

            if (!tool) {
                return null;
            }


            return {
                name: tool.short,
                value:
                    getNewValue(
                        tool,
                        item.tracking_data ||
                        {}
                    ),
                extra: "",
                icon: tool.icon,
                color:
                    "bg-cyan-50 text-blue-600",
                link:
                    `/tienich/suc-khoe/theo-doi/${tool.slug}`
            };
        };


    function formatAppointmentDate(
        dateString
    ) {

        if (!dateString) {
            return "";
        }

        const parts =
            dateString.split("-");

        if (
            parts.length !== 3
        ) {
            return dateString;
        }

        return (
            `${parts[2]}/` +
            `${parts[1]}/` +
            `${parts[0]}`
        );
    }


    const getDisplayTime =
        (item) => {

            const data =
                item?.tracking_data ||
                {};

            if (
                data.time &&
                data.date
            ) {

                return (
                    `${data.time} - ` +
                    `${data.date}`
                );
            }


            if (
                data.createdDate
            ) {

                return (
                    `Đã lưu: ` +
                    data.createdDate
                );
            }


            if (item.created_at) {

                const date =
                    new Date(
                        item.created_at
                    );

                if (
                    !Number.isNaN(
                        date.getTime()
                    )
                ) {

                    return date.toLocaleString(
                        "vi-VN",
                        {
                            hour:
                                "2-digit",
                            minute:
                                "2-digit",
                            day:
                                "2-digit",
                            month:
                                "2-digit",
                            year:
                                "numeric"
                        }
                    );
                }
            }

            return "";
        };


    useEffect(() => {

        const load =
            async () => {

                setLoadingRecent(
                    true
                );


                if (
                    syncWithAccount
                ) {

                    const result =
                        await getAllHealthHistory();

                    if (
                        result.success
                    ) {

                        const visible =
                            (
                                result.data ||
                                []
                            ).filter(
                                (item) =>
                                    getTrackingInfo(
                                        item
                                    ) !== null
                            );

                        setRecentData(
                            visible.slice(
                                0,
                                9
                            )
                        );

                    } else {

                        setRecentData(
                            []
                        );
                    }

                } else {

                    setRecentData(
                        []
                    );
                }


                setLoadingRecent(
                    false
                );
            };


        load();

        // eslint-disable-next-line
    }, [syncWithAccount]);


    return (

        <div className="max-w-screen-2xl mx-auto px-3 md:px-5 pt-16 md:pt-4 pb-10">


            <div className="relative overflow-hidden bg-gradient-to-r from-blue-700 via-blue-600 to-cyan-500 rounded-2xl px-5 py-6 md:p-9 text-white">

                <div className="max-w-3xl">

                    <div className="flex items-center">

                        <div className="w-12 h-12 md:w-14 md:h-14 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center text-2xl">

                            <HeartOutlined />

                        </div>


                        <div className="ml-4">

                            <p className="text-[11px] md:text-sm font-semibold opacity-90">

                                TIỆN ÍCH Y KHOA

                            </p>


                            <h1 className="text-[27px] md:text-3xl font-bold">

                                Theo dõi sức khỏe

                            </h1>

                        </div>

                    </div>


                    <p className="text-sm md:text-base opacity-90 leading-6 mt-4">

                        Ghi lại các chỉ số, thói quen và thông tin sức khỏe
                        để theo dõi sự thay đổi theo thời gian.

                    </p>


                    <div className="bg-white rounded-2xl h-12 flex items-center px-4 mt-5">

                        <SearchOutlined className="text-gray-400" />


                        <input
                            value={search}
                            onChange={
                                (e) =>
                                    setSearch(
                                        e.target.value
                                    )
                            }
                            placeholder="Tìm huyết áp, nhiệt độ, giấc ngủ..."
                            className="flex-1 ml-3 outline-none bg-transparent text-gray-700 text-sm"
                        />

                    </div>

                </div>

            </div>


            <div className="flex items-end justify-between mt-6">

                <div>

                    <h2 className="text-[21px] md:text-2xl font-bold text-gray-900">

                        Công cụ theo dõi

                    </h2>


                    <p className="text-gray-500 text-[13px] md:text-sm mt-1">

                        {filteredTools.length} tiện ích

                    </p>

                </div>


                <div className="hidden md:flex items-center text-sm text-blue-600 font-semibold">

                    <AppstoreOutlined className="mr-2" />

                    Bộ theo dõi sức khỏe

                </div>

            </div>


            {filteredTools.length > 0 ? (

                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 md:gap-4 mt-4">

                    {filteredTools.map(
                        (tool) => (

                            <a
                                key={tool.link}
                                href={tool.link}
                                className="group bg-white border border-gray-200 rounded-2xl p-3 md:p-5 block text-black hover:border-blue-200 hover:shadow-lg transition"
                            >

                                <div
                                    className={`w-11 h-11 md:w-12 md:h-12 rounded-xl flex items-center justify-center text-xl md:text-2xl ${tool.color}`}
                                >

                                    {tool.icon}

                                </div>


                                <div className="flex items-start justify-between mt-3">

                                    <h3 className="font-bold text-[14px] md:text-base leading-5 text-gray-900">

                                        {tool.name}

                                    </h3>


                                    <RightOutlined className="text-gray-300 text-xs mt-1 ml-2 group-hover:text-blue-500" />

                                </div>


                                <p className="text-gray-500 text-[11px] md:text-[13px] leading-5 mt-1.5">

                                    {tool.description}

                                </p>

                            </a>

                        )
                    )}

                </div>

            ) : (

                <div className="bg-white border rounded-2xl p-10 text-center mt-4">

                    <SearchOutlined className="text-3xl text-gray-300" />

                    <p className="font-semibold text-gray-700 mt-3">

                        Không tìm thấy tiện ích

                    </p>

                </div>

            )}


            <div className="mt-9">

                <h2 className="text-[21px] md:text-2xl font-bold text-gray-900">

                    Theo dõi gần đây

                </h2>


                <p className="text-gray-500 text-[13px] md:text-sm mt-1">

                    {syncWithAccount
                        ? "Các bản ghi gần nhất được lưu theo tài khoản."
                        : "Đăng nhập để xem dữ liệu đồng bộ theo tài khoản."
                    }

                </p>


                {loadingRecent ? (

                    <div className="bg-white border rounded-2xl p-8 text-center mt-4 text-gray-500">

                        Đang tải dữ liệu...

                    </div>

                ) : recentData.length ===
                  0 ? (

                    <div className="bg-white border rounded-2xl p-8 text-center mt-4">

                        <LineChartOutlined className="text-3xl text-gray-300" />

                        <p className="font-semibold text-gray-700 mt-3">

                            Chưa có dữ liệu theo dõi

                        </p>

                    </div>

                ) : (

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">

                        {recentData.map(
                            (item) => {

                                const info =
                                    getTrackingInfo(
                                        item
                                    );

                                if (!info) {
                                    return null;
                                }


                                return (

                                    <a
                                        key={
                                            item.health_tracking_id
                                        }
                                        href={
                                            info.link
                                        }
                                        className="bg-white border rounded-2xl p-4 hover:shadow-md block text-black"
                                    >

                                        <div className="flex items-start">

                                            <div
                                                className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${info.color}`}
                                            >

                                                {info.icon}

                                            </div>


                                            <div className="ml-3 flex-1 min-w-0">

                                                <p className="text-[12px] font-semibold text-gray-500">

                                                    {info.name}

                                                </p>


                                                <p className="font-bold text-[17px] text-gray-900 mt-1 break-words">

                                                    {info.value}

                                                </p>


                                                {info.extra && (

                                                    <p className="text-[12px] text-gray-500 mt-1">

                                                        {info.extra}

                                                    </p>

                                                )}


                                                <p className="text-[11px] text-gray-400 mt-2">

                                                    {getDisplayTime(
                                                        item
                                                    )}

                                                </p>

                                            </div>

                                        </div>

                                    </a>

                                );
                            }
                        )}

                    </div>

                )}

            </div>


            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 md:p-5 mt-8">

                <p className="font-bold text-gray-800">

                    Theo dõi để quan sát xu hướng

                </p>


                <p className="text-[13px] md:text-sm text-gray-600 leading-6 mt-1">

                    Các tiện ích giúp ghi lại thông tin theo thời gian.
                    Một chỉ số đơn lẻ không đủ để tự chẩn đoán bệnh.
                    Khi có triệu chứng đáng lo ngại hoặc tình trạng sức khỏe thay đổi,
                    hãy liên hệ nhân viên y tế phù hợp.

                </p>

            </div>

        </div>
    );
}
